"use client";

import Box from "@mui/material/Box";
import ChartRenderer from "../chartRenderer";
import type { ChartData } from "../chartRenderer";
import {
  IconButton,
  Stack,
  Tooltip,
  Typography,
  TextField,
  Button,
  Chip,
} from "@mui/material";
import Image from "next/image";
import { formatRelativeTime } from "@/utils";
import gaService from "@/api/services/ga.service";
import { ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";
import { useEffect, useState } from "react";
import feedbackService from "@/api/services/feedback.service";
import ReactMarkdown from "react-markdown";

type RawChartsShape = {
  dynamic_charts?: ChartData[];
  static_chart?: ChartData[];
};

interface MessageItemProps {
  role: "assistant" | "human" | "ai" | "user";
  message: string | string[];
  charts?: ChartData[] | RawChartsShape | null;
  time?: string;
  messageId?: string;
  autoScroll?: (delay: number) => void;
}

const positiveReasons = [
  "Clear explanation",
  "Helpful insights",
  "Good charts",
  "Actionable next steps",
];

const negativeReasons = [
  "Data seems incorrect",
  "Insight not useful",
  "UI confusing / unclear",
  "Loading too slow",
];

const MessageItem = ({
  role,
  message,
  charts,
  time,
  messageId,
  autoScroll
}: MessageItemProps) => {
  const isHuman = role === "user" || role === "human";

  const normalizedText = Array.isArray(message)
    ? message.join("\n\n")
    : message ?? "";

  // normalize charts
  let normalizedCharts: ChartData[] = [];
  if (Array.isArray(charts)) {
    normalizedCharts = charts;
  } else if (charts && typeof charts === "object") {
    const raw = charts as RawChartsShape;
    const dynamicCharts = raw.dynamic_charts ?? [];
    const staticCharts = raw.static_chart ?? [];
    normalizedCharts = [...dynamicCharts, ...staticCharts];
  }

  // --------- feedback state ----------
  const [feedbackType, setFeedbackType] = useState<"like" | "dislike" | null>(
    null
  );
  const [showSuggestionBox, setShowSuggestionBox] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [isSendingQuickFeedback, setIsSendingQuickFeedback] = useState(false);
  const [isSendingSuggestion, setIsSendingSuggestion] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  // hide "thanks" after 5s
  useEffect(() => {
    if (!showThanks) return;
    const t = setTimeout(() => setShowThanks(false), 5000);
    return () => clearTimeout(t);
  }, [showThanks]);

  const handleToggleReason = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const sendFeedback = async (
    feedbackValue: "true" | "false",
    suggestionText?: string
  ) => {
    if (!messageId) {
      console.warn("messageId is required to send feedback");
      return;
    }

    try {
      await feedbackService.sendFeedback({
        message_id: messageId,
        feedback: feedbackValue,
        suggestion: suggestionText?.trim() || undefined,
      });

      // optional GA event
      try {
        gaService?.chatFeedback?.(
          messageId,
          feedbackValue === "true" ? "like" : "dislike",
          !!suggestionText?.trim()
        );
      } catch (e) {
        console.error("GA feedback event failed", e);
      }

      setShowThanks(true);
    } catch (err) {
      console.error("Error sending feedback", err);
    }
  };

  const handleThumbClick = async (type: "like" | "dislike") => {
    if (!messageId) return;
    const feedbackValue = type === "like" ? "true" : "false";
    setFeedbackType(type);
    
    setShowSuggestionBox(true);
    setSelectedReasons([]); // reset on change
    setSuggestion("");

    setIsSendingQuickFeedback(true);
    await sendFeedback(feedbackValue); // quick feedback without suggestion
    
    setIsSendingQuickFeedback(false);
    setTimeout(()=>{
      autoScroll?.(100)
      if (feedbackValue === "true")gaService.chatFeedbackLike(messageId) 
      else gaService.chatFeedbackDislike(messageId);
    }, 500)
  };

  const handleSuggestionSubmit = async () => {
    const hasReasons = selectedReasons.length > 0;
    const hasText = !!suggestion.trim();

    if (!feedbackType || (!hasReasons && !hasText) || !messageId) return;

    const feedbackValue = feedbackType === "like" ? "true" : "false";

    const fullSuggestion = [
      selectedReasons.join(", "),
      suggestion.trim(),
    ]
      .filter(Boolean)
      .join(" | ");

    setIsSendingSuggestion(true);
    await sendFeedback(feedbackValue, fullSuggestion);
    setIsSendingSuggestion(false);
    setSuggestion("");
    setSelectedReasons([]);
    setShowSuggestionBox(false);
    await (feedbackValue === "true" ? gaService.chatFeedbackLike(messageId, fullSuggestion) : gaService.chatFeedbackDislike(messageId, fullSuggestion));
  };

  const isLikeSelected = feedbackType === "like";
  const isDislikeSelected = feedbackType === "dislike";
  const hasAnyDetail = selectedReasons.length > 0 || !!suggestion.trim();

  const reasonsList =
    feedbackType === "like" ? positiveReasons : negativeReasons;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isHuman ? "flex-end" : "flex-start",
        mb: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isHuman ? "flex-end" : "flex-start",
          maxWidth: isHuman ? "80%" : "100%",
          flex: 1,
        }}
      >
        {/* Message bubble */}
        <Box
          sx={{
            background: isHuman
              ? "linear-gradient(88.34deg, rgba(119, 32, 255, 0.3) 0.11%, rgba(109, 151, 255, 0.3) 97.34%)"
              : "rgba(255,255,255,0.1)",
            border: isHuman ? "1px solid rgba(255,255,255,0.08)" : "none",
            p: "12px 24px",
            borderRadius: "16px",
            color: "#fff",
            fontSize: 14,
            lineHeight: 1.6,
            whiteSpace: "normal",
            "& p": { m: 0, mb: 1 },
            "& p:last-of-type": { mb: 0 },
            "& ul, & ol": { pl: 3, mb: 1, mt: 0.5 },
            "& li": { mb: 0.25 },
            "& code": {
              background: "rgba(0,0,0,0.25)",
              borderRadius: "6px",
              px: 0.75,
              py: 0.25,
              fontSize: 13,
            },
            "& pre": {
              background: "rgba(0,0,0,0.35)",
              borderRadius: "10px",
              p: 1.5,
              overflowX: "auto",
              fontSize: 13,
            },
          }}
        >
          {!isHuman && (
            <Stack
              sx={{ mt: "4px" }}
              direction={"row"}
              alignItems={"center"}
              gap={1}
              mb={1}
            >
              <Image
                src="https://sr-cdn-1.shiprocket.in/sr-promise/images/trends_ai_chat_icon.svg"
                alt="AI"
                width={24}
                height={24}
              />
              <Typography color="#8C9FFF" fontWeight={600} fontSize={14}>
                TRENDS AI
              </Typography>
            </Stack>
          )}

          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => (
                <a
                  {...props}
                  style={{ color: "#8C9FFF", textDecoration: "underline" }}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          >
            {normalizedText}
          </ReactMarkdown>

          <Stack direction={isHuman ? "row-reverse" : "row"}>
            <Typography
              variant="body2"
              fontSize={11}
              mt={0}
              color={"rgba(255,255,255,0.3)"}
            >
              {formatRelativeTime(time || Date.now(), true)}
            </Typography>
          </Stack>
        </Box>

        {/* Charts */}
        {normalizedCharts.length > 0 && (
          <Box
            sx={{
              mt: 1,
              width: isHuman ? "80%" : "100%",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            {normalizedCharts.map((chartData, idx) => (
              <Box
                key={idx}
                onMouseOver={() => {
                  // gaService.chartInteraction(
                  //   chartData.title || `chart_${idx + 1}`
                  // );
                }}
              >
                <ChartRenderer data={chartData} colorScheme="blue" />
              </Box>
            ))}
          </Box>
        )}

        {/* Feedback controls + inline suggestion */}
        {!isHuman && (
          <Box sx={{ mt: 0.75, width: "100%" }}>
            <Stack direction="row" gap={1} alignItems="center">
              <Tooltip title="Good Response">
                <span>
                  <IconButton
                    size="small"
                    disabled={isSendingQuickFeedback}
                    sx={{
                      p: 1,
                      borderRadius: "999px",
                      border: isLikeSelected
                        ? "1px solid #52C796"
                        : "1px solid transparent",
                      backgroundColor: isLikeSelected
                        ? "rgba(82,199,150,0.18)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "#52C796",
                      },
                      transition: "all 0.15s ease-out",
                    }}
                    onClick={() => handleThumbClick("like")}
                  >
                    <ThumbsUpIcon size={18} color="rgba(255,255,255,0.7)" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Bad Response">
                <span>
                  <IconButton
                    size="small"
                    disabled={isSendingQuickFeedback}
                    sx={{
                      p: 1,
                      borderRadius: "999px",
                      border: isDislikeSelected
                        ? "1px solid #FF7578"
                        : "1px solid transparent",
                      backgroundColor: isDislikeSelected
                        ? "rgba(255,117,120,0.18)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "#FF7578",
                      },
                      transition: "all 0.15s ease-out",
                    }}
                    onClick={() => handleThumbClick("dislike")}
                  >
                    <ThumbsDownIcon size={18} color="rgba(255,255,255,0.7)" />
                  </IconButton>
                </span>
              </Tooltip>

              {showThanks && (
                <Typography variant="caption" sx={{ color: "#52C796", ml: 1 }}>
                  Thanks for the feedback!
                </Typography>
              )}
            </Stack>

            {showSuggestionBox && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background:
                    feedbackType === "like"
                      ? "rgba(82,199,150,0.06)"
                      : "rgba(255,117,120,0.06)",
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      {feedbackType === "like"
                        ? "What did you like about this response?"
                        : "What went wrong with this response?"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      Optional
                    </Typography>
                  </Stack>

                  {/* Reason chips */}
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={0.75}
                    sx={{ mt: 0.5 }}
                  >
                    {reasonsList.map((reason) => {
                      const selected = selectedReasons.includes(reason);
                      return (
                        <Chip
                          key={reason}
                          label={reason}
                          size="small"
                          onClick={() => handleToggleReason(reason)}
                          sx={{
                            borderRadius: "999px",
                            fontSize: 11,
                            height: 24,
                            bgcolor: selected
                              ? "rgba(255,255,255,0.16)"
                              : "rgba(0,0,0,0.3)",
                            color: "#fff",
                            border: selected
                              ? "1px solid rgba(255,255,255,0.7)"
                              : "1px solid rgba(255,255,255,0.18)",
                          }}
                        />
                      );
                    })}
                  </Stack>

                  {/* Free text input */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ mt: 1 }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      placeholder={
                        feedbackType === "like"
                          ? "Add any specific details you found helpful…"
                          : "Tell us how we can improve this answer…"
                      }
                      value={suggestion}
                      onChange={(e) => setSuggestion(e.target.value)}
                      InputProps={{
                        sx: {
                          bgcolor: "rgba(0,0,0,0.3)",
                          color: "#fff",
                          fontSize: 13,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255,255,255,0.14)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(255,255,255,0.3)",
                          },
                        },
                      }}
                      inputProps={{ maxLength: 500 }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSuggestionSubmit}
                        disabled={isSendingSuggestion || !hasAnyDetail}
                        sx={{
                          textTransform: "none",
                          fontSize: 13,
                          borderRadius: "999px",
                          px: 2.5,
                        }}
                      >
                        {isSendingSuggestion ? "Submitting..." : "Submit"}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setShowSuggestionBox(false);
                          setSuggestion("");
                          setSelectedReasons([]);
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: 13,
                          borderRadius: "999px",
                          px: 1.5,
                          color: "rgba(255,255,255,0.6)",
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MessageItem;
